interface ShapeSettings {
	sawing_method: string;
	purchase_price_minimum?: string;
	selling_price_minimum?: string;
	swap_maximum_dimensions?: boolean;
}

interface SawingMethodSettings {
	purchase_price_per_m?: string;
	selling_price_per_m?: string;
}

interface OtherSettings {
	shape_purchase_price_starting_rate?: string;
	shape_selling_price_starting_rate?: string;
	enable_cutout?: boolean;
	enable_drillholes?: boolean;
}

export default class PricingGroup {
	enabled_shapes: string[];
	shape_settings: Record<string, ShapeSettings>;
	sawing_method_settings: Record<string, SawingMethodSettings>;
	other_settings: OtherSettings;

	constructor( settings: {
		enabled_shapes?: string[];
		shape_settings?: Record<string, ShapeSettings>;
		sawing_method_settings?: Record<string, SawingMethodSettings>;
		other_settings?: OtherSettings;
	} ) {
		this.enabled_shapes = settings.enabled_shapes || [];
		this.shape_settings = settings.shape_settings || {};
		this.sawing_method_settings = settings.sawing_method_settings || {};
		this.other_settings = {
			shape_purchase_price_starting_rate:
				settings.other_settings?.shape_purchase_price_starting_rate || "0",
			shape_selling_price_starting_rate:
				settings.other_settings?.shape_selling_price_starting_rate || "0",
			enable_cutout: settings.other_settings?.enable_cutout || false,
			enable_drillholes: settings.other_settings?.enable_drillholes || false,
		};
	}

	get_sawing_method_settings( sawing_method: string ): SawingMethodSettings {
		return this.sawing_method_settings[ sawing_method ] || {};
	}

	get_sawing_purchase_price_per_m( sawing_method: string ): number {
		const sawing_method_settings = this.get_sawing_method_settings(
			sawing_method
		);
		return parseFloat( sawing_method_settings.purchase_price_per_m || "0" );
	}

	get_sawing_selling_price_per_m( sawing_method: string, shape: string ): number {
		const shapeSettings = this.get_shape_settings( shape );

		if(shape === 'rectangle' && shapeSettings.sawing_method === sawing_method) {
			return 0;
		}

		const sawing_method_settings = this.get_sawing_method_settings(
			sawing_method
		);
		return parseFloat( sawing_method_settings.selling_price_per_m || "0" );
	}

	get_shape_settings( shape: string ): ShapeSettings {
		return this.shape_settings[ shape ] || {
			sawing_method: "sawing",
		};
	}

	get_sawing_method( shape: string ): string {
		const shape_settings = this.get_shape_settings( shape );
		return shape_settings.sawing_method || "sawing";
	}

	get_purchase_starting_price(): number {
		return parseFloat( this.other_settings.shape_purchase_price_starting_rate );
	}

	get_selling_starting_price(): number {
		return parseFloat( this.other_settings.shape_selling_price_starting_rate );
	}

	get_minimum_purchase_price( shape: string ): number {
		const shape_settings = this.get_shape_settings( shape );
		return parseFloat( shape_settings.purchase_price_minimum || "0" );
	}

	get_minimum_selling_price( shape: string ): number {
		const shape_settings = this.get_shape_settings( shape );
		return parseFloat( shape_settings.selling_price_minimum || "0" );
	}

	get_enabled_shapes(): string[] {
		return this.enabled_shapes;
	}

	getEnableCutouts(): boolean {
		return this.other_settings.enable_cutout;
	}

	getEnableDrillholes(): boolean {
		return this.other_settings.enable_drillholes;
	}

	swapMaximumDimensions( shape: string ): boolean {
		const shape_settings = this.get_shape_settings( shape );
		return shape_settings.swap_maximum_dimensions || false;
	}

	jsonSerialize(): {
		enabled_shapes: string[];
		shape_settings: Record<string, ShapeSettings>;
		sawing_method_settings: Record<string, SawingMethodSettings>;
		other_settings: OtherSettings;
		} {
		return {
			enabled_shapes: this.enabled_shapes,
			shape_settings: this.shape_settings,
			sawing_method_settings: this.sawing_method_settings,
			other_settings: this.other_settings,
		};
	}
}
